import { Camera } from "../Camera";

import { IRGBA } from "src/pages/FlightTimeline/TimelineUtils";

import { ScrollBar } from "./ScrollBar";
import { ScrollBarThumb } from "./ScrollBarThumb";
import { IScrollClickResponse, ScrollBarClick } from "@vmosolutions/oc-interfaces";

export class ScrollBarWrapper {
  private scrollBarThumb: ScrollBarThumb;
  private scrollBar: ScrollBar;

  private yBarModelPos: number[] = [-1.0, 1.0];
  private yThumbModelPos: number[] = [0.0, 1.0]; // this need to be calculated only on resizing of the screen

  public xBarStart!: number;

  static scrollBarWidth = 10;

  constructor(gl: WebGLRenderingContext) {
    this.scrollBarThumb = new ScrollBarThumb(gl);
    this.scrollBar = new ScrollBar(gl);
  }

  public getThumbCoordinates = (camera: Camera, maxHeight: number): number[] => {
    const [yStart, yEnd] = camera.getYRange();
    const availablePixsOfScreen: number = yEnd - yStart;
    const viewableRatio: number = availablePixsOfScreen / maxHeight;
    const thumbHeight: number = viewableRatio * 2.0;
    return [1 - thumbHeight, 1];
  };

  public getXRangeOfScrollBar = (): number[] => {
    return [0, 1];
  };

  public calculateScrollBar = (
    camera: Camera,
    canvas: HTMLCanvasElement,
    maxHeight: number,
    sizeOfBar?: number
  ): void => {
    //set the intial thumb posistion - should only have to do this when the window is resized...

    //Y - size of thumb bar...
    const [yStart, yEnd] = camera.getYRange(); //camera coordinates
    const availablePixsOfScreen: number = yEnd - yStart;
    const viewableRatio: number = availablePixsOfScreen / maxHeight;
    const thumbHeight: number = viewableRatio * 2.0;
    this.yThumbModelPos[0] = 1 - thumbHeight;

    //X - bar start...
    const screenWidthPercentageOfBar: number = ScrollBarWrapper.scrollBarWidth / canvas.width;
    this.xBarStart = 1 - screenWidthPercentageOfBar * 2.0; //want to pass to scrollBar and scrollBarThumb...
  };

  public clickedOnScrollBar = (
    gl: WebGLRenderingContext,
    camera: Camera,
    canvas: HTMLCanvasElement,
    xPos: number,
    yPos: number,
    maxHeight: number
  ): IScrollClickResponse => {
    const [yStart, yEnd] = camera.getYRange(); //camera coordinates

    const loaded: number = yStart / maxHeight;
    const xOffset: number = (this.xBarStart + 1) * 0.5;
    const heightInPix: number = yEnd - yStart;

    const thumbStart: number = (1 - this.yThumbModelPos[1]) * heightInPix * 0.5 + loaded * heightInPix;
    const thumbEnd: number = (1 - this.yThumbModelPos[0]) * heightInPix * 0.5 + loaded * heightInPix;
    const thumblength: number = thumbEnd - thumbStart;
    const midthumb: number = thumbStart + 0.5 * thumblength;

    if (xPos >= canvas.width * xOffset && xPos <= canvas.width * this.yBarModelPos[1]) {
      switch (true) {
        case yPos >= thumbStart && yPos <= thumbEnd:
          return {
            typeOfScroll: ScrollBarClick.ClickedOnThumb,
            pixelsToMove: 0,
          };
        case yPos <= thumbStart && yPos > -1:
          return {
            typeOfScroll: ScrollBarClick.ClickedOnThumb,
            pixelsToMove: (yPos - midthumb) * (maxHeight / heightInPix),
          };
        case yPos >= thumbEnd:
          return {
            typeOfScroll: ScrollBarClick.ClickedBelowThumb,
            pixelsToMove: (yPos - midthumb) * (maxHeight / heightInPix),
          };

        default:
          return {
            typeOfScroll: ScrollBarClick.ClickedOffScrollBar,
            pixelsToMove: 0,
          };
      }
    }
    return { typeOfScroll: ScrollBarClick.None, pixelsToMove: 0 };
  };

  public hoveredOnScrollBarThumb = (
    camera: Camera,
    canvas: HTMLCanvasElement,
    xPos: number,
    yPos: number,
    maxHeight: number
  ): boolean => {
    const [yStart, yEnd] = camera.getYRange(); //camera coordinates

    const loaded: number = yStart / maxHeight;
    const xOffset: number = (this.xBarStart + 1) * 0.5;
    const heightInPix: number = yEnd - yStart;

    const thumbStart: number = (1 - this.yThumbModelPos[1]) * heightInPix * 0.5 + loaded * heightInPix;
    const thumbEnd: number = (1 - this.yThumbModelPos[0]) * heightInPix * 0.5 + loaded * heightInPix;

    if (xPos >= canvas.width * xOffset && xPos <= canvas.width * this.yBarModelPos[1]) {
      switch (true) {
        case yPos >= thumbStart && yPos <= thumbEnd:
          return true;
        default:
          return false;
      }
    }
    return false;
  };

  public drawBar = (
    gl: WebGLRenderingContext,
    camera: Camera,
    canvas: HTMLCanvasElement,
    backGroundColor: IRGBA,
    thumbColor: IRGBA,
    maxHeight: number
  ): void => {
    const [yStart, yEnd] = camera.getYRange();
    if (yEnd - yStart >= maxHeight) {
      //no scroll bar - all is loaded
      return;
    }

    //background of bar positions
    const positions: number[] = [
      this.xBarStart,
      this.yBarModelPos[0],
      this.xBarStart,
      this.yBarModelPos[1],
      1.0,
      this.yBarModelPos[0],
      1.0,
      this.yBarModelPos[1],
    ];

    //thumb of scroll bar positions
    const thumbPositions: number[] = [
      this.xBarStart,
      this.yThumbModelPos[0],
      this.xBarStart,
      this.yThumbModelPos[1],
      1.0,
      this.yThumbModelPos[0],
      1.0,
      this.yThumbModelPos[1],
    ];

    gl.disable(gl.DEPTH_TEST);

    //scroll bar background
    this.scrollBar.updateBuffers(gl, positions);
    this.scrollBar.drawScene(gl, backGroundColor);

    //scroll bar thumb
    this.scrollBarThumb.updateBuffers(gl, thumbPositions);
    this.scrollBarThumb.drawScene(gl, camera, thumbColor, maxHeight);
    gl.enable(gl.DEPTH_TEST);
  };
}
